import { useMemo } from "react"

import { GradeBadge } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { ColoredBadge } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { notationLegend } from "../../constants/getNotationConfig"
import { useRelevantNetworkGradings } from "../../hooks/queries/networkGradings/useRelevantNetworkGradings"
import { getPreviousNetworkGrading } from "../../utils/networkGradingsUtils"

import * as Styled from "./WeightedAverageNotationGrade.styled"


export function WeightedAverageNotationGrade(props: WeightedAverageNotationGradeProps) {

    const {
        id,
        year,
        canSearchInPast = false,
        mustBeSurvey = false,
        notationKey,
    } = props

    const { selectedNetwork } = useNetworksStateContext()

    const {
        isLoading,
        isError,
        sortedRelevantNetworkGradings,
    } = useRelevantNetworkGradings(selectedNetwork?.slug)

    const { t } = useTranslation(I18N_NAMESPACE)

    const grade = useMemo(() => {
        if (sortedRelevantNetworkGradings) {
            if (!canSearchInPast && !mustBeSurvey) {
                const networkGrading = sortedRelevantNetworkGradings.find((ng: any) => ng.year === year)
                return networkGrading?.gradesWeightedAverage?.[notationKey]
            } else {
                const networkGrading = getPreviousNetworkGrading(sortedRelevantNetworkGradings, year, mustBeSurvey)
                return networkGrading?.gradesWeightedAverage?.[notationKey]
            }
        }

        return null
    }, [sortedRelevantNetworkGradings, canSearchInPast, mustBeSurvey, notationKey, year])

    if (isLoading || (!sortedRelevantNetworkGradings && !isError)) {
        return <Styled.Skeleton />
    }

    if (isError || sortedRelevantNetworkGradings === null) {
        return <ColoredBadge
            id={id}
            backgroundColor="error"
            text={t(I18N_NAMESPACE, "containers.weightedAverageNotationGrade.error")}
            textVariant="Desktop & Mobile/Heading/H3"
        />
    }

    return <GradeBadge legend={notationLegend} id={id} grade={grade} />
}

type WeightedAverageNotationGradeProps = {
    id?: string
    year: number
    canSearchInPast?: boolean
    mustBeSurvey?: boolean
    notationKey: string
}
